(function () {
  class VerticalTabsSection extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.getAttribute('data-id');
      this.sectionElement = document.getElementById(`vertical-tabs-${this.sectionId}`);
      this.tabHeadings = this.querySelectorAll('.vertical-tabs__tab-heading');
      this.tabContents = this.querySelectorAll('.vertical-tabs__tab-content');
      this.imageItems = this.querySelectorAll('.vertical-tabs__image-item');
      this.tabItems = this.querySelectorAll('.vertical-tabs__tab-item');
      this.autoplay = this.getAttribute('data-autoplay') === 'true';
      this.autoplayInterval = parseInt(this.getAttribute('data-autoplay-interval'), 10) * 1000 || 5000;
      this.autoplayTimer = null;
      this.progressBars = this.querySelectorAll('.vertical-tabs__progress-bar');
      this.currentIndex = 0;
      this.tabStyle = this.getAttribute('data-tab-style') || 'blank';

      this.init();

      document.addEventListener('shopify:block:select', (event) => {
        const target = event.target;
        const sectionElement = target.closest('.vertical-tabs-section');
        if (!sectionElement || sectionElement.id !== `vertical-tabs-${this.sectionId}`) return;

        const blockId = target.getAttribute('data-block-id');
        if (blockId) {
          const tabId = 'tab-' + blockId;
          this.activateTab(tabId);
          this.stopAutoplay();
        }
      });

      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.querySelector(`#vertical-tabs-${this.sectionId}`)) {
          this.init();
        }
      });
    }

    init() {
      this.tabHeadings.forEach((heading) => {
        heading.addEventListener('click', (e) => {
          const tabId = e.currentTarget.getAttribute('data-tab-id');
          this.activateTab(tabId);
          this.resetAutoplay();
        });
      });

      this.setFirstImageAspectRatio();

      if (this.tabHeadings.length > 0) {
        const firstTabId = this.tabHeadings[0].getAttribute('data-tab-id');
        this.activateTab(firstTabId);
      }

      if (this.autoplay) {
        this.startAutoplay();

        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => {
          if (this.autoplay) this.startAutoplay();
        });
      }

      this.updateContentHeight();
      window.addEventListener('resize', debounce(() => {
        this.updateContentHeight();
      }, 150), { passive: true });

      this.addEventListener('transitionend', (e) => {
        if (e.target.classList.contains('vertical-tabs__tab-content')) {
          this.updateContentHeight();
        }
      });
    }

    setFirstImageAspectRatio() {
      const firstImage = this.querySelector('.vertical-tabs__image-item img');
      if (firstImage) {
        const setRatio = () => {
          const w = firstImage.naturalWidth || firstImage.width;
          const h = firstImage.naturalHeight || firstImage.height;
          if (w && h) {
            const wrapper = this.querySelector('.vertical-tabs__image-wrapper');
            if (wrapper) {
              wrapper.style.setProperty('--first-image-aspect-ratio', `${w}/${h}`);
            }
          }
        };
        if (firstImage.complete) {
          setRatio();
        } else {
          firstImage.addEventListener('load', setRatio, { once: true });
        }
      }
    }

    activateTab(tabId) {
      let activeIndex = 0;

      this.tabHeadings.forEach((heading, index) => {
        const isActive = heading.getAttribute('data-tab-id') === tabId;
        heading.classList.toggle('vertical-tabs__tab-heading--active', isActive);
        if (isActive) activeIndex = index;
      });

      this.tabContents.forEach((content) => {
        const isActive = content.getAttribute('data-tab-id') === tabId;
        content.classList.toggle('vertical-tabs__tab-content--active', isActive);
      });

      this.imageItems.forEach((item) => {
        const isActive = item.getAttribute('data-tab-id') === tabId;
        item.classList.toggle('vertical-tabs__image-item--active', isActive);
      });

      this.progressBars.forEach((bar) => {
        bar.style.transition = 'none';
        bar.style.width = '0%';
      });

      this.currentIndex = activeIndex;
      this.updateContentHeight();
    }

    startAutoplay() {
      this.stopAutoplay();
      const activeProgressBar = this.tabItems[this.currentIndex]?.querySelector('.vertical-tabs__progress-bar');
      if (activeProgressBar) {
        activeProgressBar.style.transition = 'none';
        activeProgressBar.style.width = '0%';
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            activeProgressBar.style.transition = `width ${this.autoplayInterval}ms linear`;
            activeProgressBar.style.width = '100%';
          });
        });
      }

      this.autoplayTimer = setTimeout(() => {
        const nextIndex = (this.currentIndex + 1) % this.tabHeadings.length;
        const nextTabId = this.tabHeadings[nextIndex].getAttribute('data-tab-id');
        this.activateTab(nextTabId);
        this.startAutoplay();
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      this.progressBars.forEach((bar) => {
        bar.style.transition = 'none';
        bar.style.width = '0%';
      });
    }

    resetAutoplay() {
      if (this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    updateContentHeight() {
      const imageWrapper = this.querySelector('.vertical-tabs__image-wrapper');
      const tabsColumn = this.querySelector('.vertical-tabs__tabs-column');
      if (tabsColumn && imageWrapper) {
        const contentHeight = tabsColumn.scrollHeight;
        this.style.setProperty('--content-height', `${contentHeight}px`);
      }
    }
  }

  if (!customElements.get('vertical-tabs-section')) {
    customElements.define('vertical-tabs-section', VerticalTabsSection);
  }
})();